import sortBy from "lodash/sortBy";
import some from "lodash/some";

//
// Action types
//

export const INITIALIZE_MODAL = "INITIALIZE_MODAL";
export const SHOW_MODAL = "SHOW_MODAL";
export const HIDE_MODAL = "HIDE_MODAL";
export const REMOVE_MODAL = "REMOVE_MODAL";
export const RESTORE_MODAL = "RESTORE_MODAL";
export const SET_MODAL_TITLE = "SET_MODAL_TITLE";

//
// Action creators
//

export function initializeModal(id) {
  return {
    type: INITIALIZE_MODAL,
    id
  };
}

export function showModal(id) {
  return {
    type: SHOW_MODAL,
    id
  };
}

export function hideModal(id) {
  return {
    type: HIDE_MODAL,
    id
  };
}

export function removeModal(id) {
  return {
    type: REMOVE_MODAL,
    id
  };
}

export function restoreModal(id) {
  return {
    type: RESTORE_MODAL,
    id
  };
}

export function setModalTitle(id, title) {
  return {
    type: SET_MODAL_TITLE,
    id,
    title
  };
}

// Returns the provided state with every active modals position updated so that
// we have a consistent positions set.
//
function reorderModalPositions(state) {
  const modals = sortBy(Object.values(state.activeModals), modal => modal.position);

  modals.forEach((modal, index) => {
    state.activeModals[modal.id].position = index;
  });

  return state;
}

function openLastActiveModal(state) {
  // Don't do anything if we have no active modal
  if (!Object.keys(state.activeModals).length) return;

  // If a modal is already open, don't do anything
  if (some(state.activeModals, modal => modal.isOpen)) return;

  // Get a sorted list of all modals
  const modals = sortBy(Object.values(state.activeModals), modal => modal.position);

  // Set the last modal as open
  const newActiveModal = modals[modals.length - 1];
  newActiveModal.isOpen = true;
}

function initializeModalHandler(state, id) {
  const newState = { ...state };
  if (!newState.activeModals) newState.activeModals = {};

  const position = Object.keys(newState.activeModals).length;
  newState.activeModals[id] = { id, position };

  return newState;
}

function showModalHandler(state, id) {
  const newState = { ...state };
  const targetModal = newState.activeModals[id];

  targetModal.isOpen = true;

  Object.keys(newState.activeModals).forEach(id => {
    const modal = newState.activeModals[id];

    if (modal.id !== targetModal.id) {
      modal.isOpen = false;
    }
  });

  return reorderModalPositions(newState);
}

function hideModalHandler(state, id) {
  const newState = { ...state };
  const targetModal = newState.activeModals[id];

  targetModal.isOpen = false;

  return reorderModalPositions(newState);
}

function removeModalHandler(state, id) {
  const newState = { ...state };
  delete newState.activeModals[id];

  openLastActiveModal(newState);

  return reorderModalPositions(newState);
}

function restoreModalHandler(state, id) {
  const newState = { ...state };
  const targetModal = newState.activeModals[id];

  Object.keys(newState.activeModals).forEach(id => {
    const modal = newState.activeModals[id];

    if (modal.position > targetModal.position) {
      modal.shouldBeClosed = true;
    }
  });

  return newState;
}

function setModalTitleHandler(state, id, title) {
  const newState = { ...state };
  newState.activeModals[id].title = title;
  return newState;
}

//
// Reducer and handlers
//
export default function modalsReducer(state, action) {
  switch (action && action.type) {
    case INITIALIZE_MODAL:
      return initializeModalHandler(state, action.id);

    case SHOW_MODAL:
      return showModalHandler(state, action.id);

    case HIDE_MODAL:
      return hideModalHandler(state, action.id);

    case REMOVE_MODAL:
      return removeModalHandler(state, action.id);

    case RESTORE_MODAL:
      return restoreModalHandler(state, action.id);

    case SET_MODAL_TITLE:
      return setModalTitleHandler(state, action.id, action.title);

    default:
      return state || { activeModals: {} };
  }
}
